import React from "react";
import { Controller } from "react-hook-form";
import { InputV3 } from "components/common/input/InputV3";
import { SearchableDropdown } from "components/common/searchableDropdown/SearchableDropdown";
import { TextArea } from "components/common/textArea/TextArea";
import { ProductionBaseField } from "components/common/ProductionBaseField";
import { getProductionTypeOptions } from "../CreateProjectSheet.utils";

interface ProjectDetailsSectionProps {
  control: any;
  errors: any;
  mode: "project" | "version";
  /** "nameOnly" for quick blank-project create; "full" for import / version flows. */
  variant?: "full" | "nameOnly";
}

export const ProjectDetailsSection: React.FC<ProjectDetailsSectionProps> = ({
  control,
  errors: _errors,
  mode,
  variant = "full",
}) => {
  const productionTypeOptions = getProductionTypeOptions();

  if (variant === "nameOnly") {
    return (
      <div className="flex flex-col gap-4">
        <Controller
          name="name"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <InputV3
              {...field}
              label="Project name"
              placeholder="Enter project name"
              error={!!error}
              errorMessage={error?.message}
              maxLength={165}
              disabled={mode === "version"}
              required
            />
          )}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-medium text-foreground">Project Details</h3>
      <div className="flex flex-col gap-4">
        <Controller
          name="name"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <InputV3
              {...field}
              label="Project Name"
              placeholder="Enter project name"
              error={!!error}
              errorMessage={error?.message}
              maxLength={165}
              disabled={mode === "version"}
              required
            />
          )}
        />

        <Controller
          name="production_type"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-muted-foreground">Production Type *</label>
              <SearchableDropdown
                name="production_type"
                options={productionTypeOptions}
                placeholder="Select production type"
                value={field.value}
                onChange={field.onChange}
                noFormContext
              />
              {error && <div className="text-xs text-danger">{error.message}</div>}
            </div>
          )}
        />

        <Controller
          name="features.production_location"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <div className="flex flex-col gap-2">
              <ProductionBaseField
                value={{
                  name: value?.name ?? "",
                  place_id: value?.place_id ?? null,
                }}
                onChange={onChange}
                helperText="Sets budget and tax defaults."
              />
              {error ? <div className="text-xs text-danger">{error.message}</div> : null}
            </div>
          )}
        />
      </div>

      <Controller
        name="description"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextArea
            {...field}
            label="Description (Optional)"
            placeholder="Enter project description"
            maxLength={512}
            rows={4}
            alert={error ? { type: "error", message: error.message || "" } : undefined}
          />
        )}
      />
    </div>
  );
};
